import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface SearchResult {
  user_id: string;
  username: string;
  level: number;
  xp: number;
  is_following: boolean;
}

export function useFriends() {
  const { user } = useAuth();
  const [followingCount, setFollowingCount] = useState(0);

  const fetchFollowingCount = useCallback(async () => {
    if (!user) return;
    const { count } = await supabase
      .from('user_follows')
      .select('*', { count: 'exact', head: true })
      .eq('follower_id', user.id);
    setFollowingCount(count ?? 0);
  }, [user]);

  useEffect(() => {
    fetchFollowingCount();
  }, [fetchFollowingCount]);

  const searchUsers = async (query: string): Promise<SearchResult[]> => {
    if (!query || query.length < 2) return [];
    const { data, error } = await supabase.rpc('search_users', {
      search_query: query,
      result_limit: 20,
    });
    if (error) {
      console.error('Search error:', error);
      return [];
    }
    return (data || []) as SearchResult[];
  };

  const followUser = async (targetUserId: string) => {
    if (!user) return false;
    const { error } = await supabase
      .from('user_follows')
      .insert({ follower_id: user.id, following_id: targetUserId });
    if (error) {
      if (error.code === '23505') {
        toast.info('Already following this user');
      } else {
        toast.error('Failed to follow');
        console.error('Follow error:', error);
      }
      return false;
    }
    toast.success('Following!');
    fetchFollowingCount();
    return true;
  };

  const unfollowUser = async (targetUserId: string) => {
    if (!user) return false;
    const { error } = await supabase
      .from('user_follows')
      .delete()
      .eq('follower_id', user.id)
      .eq('following_id', targetUserId);
    if (error) {
      toast.error('Failed to unfollow');
      return false;
    }
    toast.success('Unfollowed');
    fetchFollowingCount();
    return true;
  };

  return { searchUsers, followUser, unfollowUser, followingCount, refetch: fetchFollowingCount };
}
